// Which story lengths are offered (owner decision 2026-10-16): the 8-page book is the affordable option for
// low-price countries (lib/pricing/low-price-countries.ts); everywhere else books are 16 or 24 pages.
import { isLowPriceCountry } from './low-price-countries';
import { COMIC_PAGE_COUNTS, COMIC_SQUARE_PAGE_COUNT, type ComicPageCount, type AnyComicPageCount, type ComicShape } from '../../types/manga';

export const DEFAULT_STORY_PAGE_COUNT = 16;

export function allowedStoryPageCounts(country: string | null): number[] {
  return isLowPriceCountry(country) ? [8, 16, 24] : [16, 24];
}

/** The page count to generate: the request if it is allowed for this country, else the 16-page default. */
export function enforceStoryPageCount(requested: unknown, country: string | null): number {
  return typeof requested === 'number' && allowedStoryPageCounts(country).includes(requested)
    ? requested
    : DEFAULT_STORY_PAGE_COUNT;
}

/** Comic lengths offered for this country: the picture-book rule restricted to the lengths a comic can be (8 / 16). */
export function allowedComicPageCounts(country: string | null): ComicPageCount[] {
  const story = allowedStoryPageCounts(country);
  return COMIC_PAGE_COUNTS.filter((n) => story.includes(n));
}

/** Page options for the picker: the tall lengths allowed for the country, then the square 24 when the flag is on. */
export function comicPageOptionsFromCounts(
  tallCounts: number[],
  squareOn: boolean,
): Array<{ pageCount: AnyComicPageCount; shape: ComicShape }> {
  const tall = COMIC_PAGE_COUNTS.filter((n) => tallCounts.includes(n)).map((n) => ({ pageCount: n as AnyComicPageCount, shape: 'tall' as ComicShape }));
  return squareOn ? [...tall, { pageCount: COMIC_SQUARE_PAGE_COUNT, shape: 'square' as ComicShape }] : tall;
}

/** The comic page count to make: the request if allowed for this country, else 16. Never refuses. */
export function enforceComicPageCount(requested: unknown, country: string | null, squareOn: boolean = false): AnyComicPageCount {
  if (squareOn && requested === COMIC_SQUARE_PAGE_COUNT) return COMIC_SQUARE_PAGE_COUNT;
  const allowed = allowedComicPageCounts(country);
  return allowed.find((n) => n === requested) ?? (DEFAULT_STORY_PAGE_COUNT as ComicPageCount);
}

/**
 * The page count to record on a book. Supplied story pages came from the enforced story route, so the real
 * story-page count (pages with pageNumber > 0) wins over the client's pageCount. Without story pages the
 * client's pageCount is enforced for the country before anything generates from it.
 */
export function bookPageCountFor(storyPages: unknown, requested: unknown, country: string | null): number {
  if (Array.isArray(storyPages)) {
    const n = storyPages.filter((p: any) => typeof p?.pageNumber === 'number' && p.pageNumber > 0).length;
    if (n > 0) return n;
  }
  return enforceStoryPageCount(requested, country);
}
